import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type JournoQuery, journoQueriesApi } from "../lib/api";

export function JournoPage() {
  const [statusFilter, setStatusFilter] = useState<string>("new");
  const q = useQuery({
    queryKey: ["journo-queries"],
    queryFn: journoQueriesApi.list,
    refetchInterval: 5 * 60_000,
  });

  const visible = q.data?.queries.filter((qq) => statusFilter === "all" || qq.status === statusFilter) ?? [];

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Journalist queries</h1>
          <p className="text-ink-600 text-sm mt-1">
            Pipelined from r/JournoRequests via daily <code className="bg-ink-100 px-1 rounded text-xs">rankforge haro --persist</code>.
            Each landed pitch is a high-DR backlink.
          </p>
        </div>
        {q.data && <div className="text-right">
          <div className="text-2xl font-display font-bold">{visible.length}</div>
          <div className="text-xs uppercase tracking-wider text-ink-500">{statusFilter}</div>
        </div>}
      </div>

      <div className="bg-white rounded-xl border border-ink-100 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-ink-500 font-medium">Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-ink-200 rounded-md text-sm bg-white">
            <option value="new">new</option>
            <option value="pitched">pitched</option>
            <option value="landed">landed</option>
            <option value="dead">dead</option>
            <option value="all">all</option>
          </select>
        </label>
      </div>

      {q.isLoading && <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((q.error as Error).message)}
        </div>
      )}
      {q.data && visible.length === 0 && (
        <div className="bg-white rounded-xl border border-ink-100 p-8 text-center">
          <p className="text-ink-700 font-medium">No "{statusFilter}" queries.</p>
          <p className="text-ink-500 text-sm mt-1">
            Run <code className="bg-ink-100 px-1.5 py-0.5 rounded text-xs">rankforge haro --persist --niches plumbing,roofing --states TX,FL,NC</code> on
            a daily cron to populate.
          </p>
        </div>
      )}
      <div className="space-y-3">
        {visible.map((qq) => <QueryCard key={qq.id} query={qq} />)}
      </div>
    </div>
  );
}

function QueryCard({ query }: { query: JournoQuery }) {
  const qc = useQueryClient();
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(query.notes ?? "");
  const [landedUrl, setLandedUrl] = useState(query.landedUrl ?? "");
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof journoQueriesApi.update>[1]) => journoQueriesApi.update(query.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["journo-queries"] }),
  });

  const ageHours = Math.round((Date.now() - new Date(query.postedAt).getTime()) / 3_600_000);
  const ageLabel = ageHours < 24 ? `${ageHours}h ago` : `${Math.round(ageHours / 24)}d ago`;
  const tone: Record<string, string> = {
    new: "bg-blue-100 text-blue-800",
    pitched: "bg-accent-100 text-accent-700",
    landed: "bg-green-100 text-green-800",
    dead: "bg-ink-100 text-ink-500",
  };

  return (
    <div className="bg-white rounded-xl border border-ink-100 shadow-sm overflow-hidden">
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <a href={query.url} target="_blank" rel="noreferrer" className="font-medium text-ink-900 hover:text-accent">
            {query.title} <span aria-hidden>↗</span>
          </a>
          <div className="flex gap-2 items-center flex-shrink-0">
            <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-bold ${tone[query.status] ?? tone.dead}`}>
              {query.status} · score {query.score}
            </span>
            <span className="text-xs text-ink-500">{ageLabel}</span>
          </div>
        </div>
        {query.matched && query.matched.length > 0 && (
          <p className="text-xs text-ink-500 mb-2">matched: {query.matched.join(" · ")}</p>
        )}
        {query.bodySnippet && (
          <p className="text-sm text-ink-700 leading-relaxed">{query.bodySnippet.slice(0, 360)}{query.bodySnippet.length > 360 && "…"}</p>
        )}
      </div>
      <div className="border-t border-ink-100 bg-ink-50/50 px-4 py-2 flex items-center gap-2 text-xs">
        <button onClick={() => update.mutate({ status: "pitched" })}
                disabled={query.status === "pitched" || update.isPending}
                className="px-2 py-1 bg-accent text-white rounded font-medium disabled:opacity-50 hover:bg-accent-600">
          Mark pitched
        </button>
        <button onClick={() => update.mutate({ status: "landed" })}
                disabled={query.status === "landed" || update.isPending}
                className="px-2 py-1 bg-green-600 text-white rounded font-medium disabled:opacity-50 hover:bg-green-700">
          Mark landed
        </button>
        <button onClick={() => update.mutate({ status: "dead" })}
                disabled={query.status === "dead" || update.isPending}
                className="px-2 py-1 bg-ink-300 text-ink-700 rounded font-medium disabled:opacity-50 hover:bg-ink-400">
          Skip
        </button>
        <button onClick={() => setShowNotes((s) => !s)}
                className="ml-auto text-ink-500 hover:text-ink-900">
          {showNotes ? "Hide notes" : "Notes"}
        </button>
      </div>
      {showNotes && (
        <div className="px-4 py-3 border-t border-ink-100 space-y-2">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Pitch summary, follow-up date, etc."
                    rows={2}
                    className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm" />
          <input value={landedUrl} onChange={(e) => setLandedUrl(e.target.value)}
                 placeholder="If landed: published URL"
                 className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm" />
          <button onClick={() => update.mutate({ notes, landedUrl })}
                  disabled={update.isPending}
                  className="px-3 py-1 bg-ink-900 text-white rounded text-xs font-medium">
            {update.isPending ? "Saving…" : "Save notes"}
          </button>
        </div>
      )}
    </div>
  );
}
